import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Displays a formula with optional computed values substituted inline.
 *
 * Two modes:
 *  - **Inline**: compact label beneath a cell
 *  - **Tooltip**: hover-activated popover showing the formula breakdown
 *
 * The tooltip renders via a portal to `document.body` so it is never clipped
 * by ancestor `overflow` rules (e.g. the Table wrapper's `overflow-x-auto`).
 */

export interface FormulaVariable {
  readonly label: string;
  readonly value: string;
}

interface FormulaTooltipProps {
  readonly formula: string;
  readonly variables?: readonly FormulaVariable[];
  readonly result?: string;
  readonly children: React.ReactNode;
}

export function FormulaTooltip({ formula, variables, result, children }: FormulaTooltipProps) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const rect =
    visible && triggerRef.current !== null ? triggerRef.current.getBoundingClientRect() : null;

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex cursor-help"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <span className="ml-0.5 mt-px text-[9px] text-ink-faint align-super">ƒ</span>
      {visible &&
        rect !== null &&
        createPortal(
          <span
            className="fixed z-50 w-max max-w-xs rounded-lg border border-edge bg-surface-base shadow-lg px-3 py-2.5 text-left pointer-events-none"
            style={{
              top: rect.top - 8,
              left: rect.left + rect.width / 2,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <span className="block text-[10px] font-medium text-ink-faint uppercase tracking-wide mb-1">
              Formula
            </span>
            <code className="block text-xs font-mono text-ink">{formula}</code>
            {variables !== undefined && variables.length > 0 && (
              <span className="block mt-1.5 space-y-0.5">
                {variables.map((v) => (
                  <span key={v.label} className="flex justify-between gap-4 text-[11px]">
                    <span className="text-ink-muted">{v.label}</span>
                    <span className="font-mono text-ink">{v.value}</span>
                  </span>
                ))}
              </span>
            )}
            {result !== undefined && (
              <span className="block mt-1.5 pt-1.5 border-t border-edge">
                <span className="flex justify-between gap-4 text-xs">
                  <span className="font-medium text-ink-muted">Result</span>
                  <span className="font-mono font-semibold text-profit">{result}</span>
                </span>
              </span>
            )}
          </span>,
          document.body,
        )}
    </span>
  );
}

interface ShowFormulasToggleProps {
  readonly enabled: boolean;
  readonly onToggle: (enabled: boolean) => void;
}

export function ShowFormulasToggle({ enabled, onToggle }: ShowFormulasToggleProps) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!enabled)}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
        enabled
          ? 'border-profit/40 bg-profit/10 text-profit'
          : 'border-edge bg-surface-base text-ink-muted hover:text-ink'
      }`}
      title="Toggle formula display (like Excel Ctrl+`)"
    >
      <span className="font-mono text-sm leading-none">ƒx</span>
      {enabled ? 'Formulas' : 'Values'}
    </button>
  );
}
